import React from "react";
import { PlayCircle, User, Tags, Sparkles, Link2, MapPin, AtSign } from "lucide-react";
import { FeatureHero, FeatureRow, IconGrid, PhotoBand, ClosingCta, FeatureFooterNav, Shot } from "@/components/features/FeatureKit";
import { ProfileFieldsMock } from "@/components/features/mocks";

const PHOTO = "https://images.unsplash.com/photo-1573496546038-82f9c39f6365?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1800";

export default function PersonalProfilesFeature() {
  return (
    <div className="overflow-x-hidden" data-testid="feature-personal-profiles-page">
      <FeatureHero
        eyebrow="Personal Profiles"
        icon={User}
        title="Your profile should feel like you—not a résumé template."
        copy="Startup Otter Personal Profiles give you more ways to show who you are, what you know, and how you participate in the startup ecosystem."
        ctaLabel="Create Your Profile"
        ctaTo="/register"
        visual={<Shot src="/features/personal-profile.png" alt="A Startup Otter personal profile with intro video, industries and skills" url="startupotter.com/p/maya-chen" testId="personal-profile-shot" priority />}
        testId="personal-profiles-hero"
      />

      <FeatureRow
        testId="personal-video"
        label="Intro video"
        icon={PlayCircle}
        title="Put a face—and a voice—to the name."
        copy="Upload an intro video and tell your story in your own words. Explain what you do, what you're building, or what brings you to the community."
        visual={
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="mock-intro-video">
            <div className="flex aspect-video items-center justify-center rounded-xl bg-gradient-to-br from-primary/25 to-primary/5">
              <PlayCircle className="h-14 w-14 text-primary" aria-hidden="true" />
            </div>
            <p className="mt-4 text-sm text-muted-foreground">Intro Video · YouTube link or direct upload</p>
          </div>
        }
      />

      <IconGrid
        testId="personal-substance"
        tone="tint"
        title="The parts people actually want to read."
        intro="Three fields do most of the work — and all three feed how you get discovered."
        items={[
          { icon: Sparkles, title: "More than a job title.", copy: "Share your background and experience in a way that actually fits your story—not a rigid résumé format." },
          { icon: Tags, title: "Tag the industries you know.", copy: "Add industries you work in, understand, or care about so relevant people can discover you more easily." },
          { icon: Sparkles, title: "Show what you're good at.", copy: "Highlight the skills and expertise you bring to the ecosystem and help others understand where you can add value." },
        ]}
      />

      <FeatureRow
        testId="personal-handle"
        tone="dark"
        flip
        label="Identity"
        icon={AtSign}
        title="Your own Startup Otter identity."
        copy="Your unique @handle follows you across profiles, posts, conversations, search, and the community."
        visual={<Shot src="/features/chatter.png" alt="An @handle appearing across posts and conversations on Startup Otter" url="startupotter.com/chatter" dark testId="personal-handle-shot" />}
      />

      <PhotoBand
        testId="personal-local"
        src={PHOTO}
        alt="Two founders talking by a window"
        kicker="Location"
        title="Be local when it matters."
        copy="Add your location and tap into location-aware discovery and Startup Otter's Local Mode."
      />

      <FeatureRow
        testId="personal-links"
        label="Links"
        icon={Link2}
        title="Bring your online presence with you."
        copy="Add your social media and other important links so people can explore more of your work beyond Startup Otter."
        visual={<ProfileFieldsMock title="Links" testId="mock-links" items={[["Website", "mayachen.co"], ["LinkedIn", "in/mayachen"], ["X", "@mayachen"], ["Location", "Portland, OR, US"]]} />}
      />

      <IconGrid
        testId="personal-outcomes"
        tone="brand"
        columns={3}
        title="A profile that does something."
        items={[
          { icon: MapPin, title: "Get found locally", copy: "Show up for people searching your city, your industry and your skills." },
          { icon: User, title: "Get understood", copy: "Video, story, industries and skills together say more than a title ever could." },
          { icon: Sparkles, title: "Get invited in", copy: "Relevant needs, events and conversations find their way to you." },
        ]}
      />

      <ClosingCta
        testId="personal-profiles-closing"
        title="Not a digital résumé. A living identity."
        copy="Your profile can post, friend, follow, get discovered, share, and participate in the ecosystem around you."
        ctaLabel="Create Your Profile"
        ctaTo="/register"
      />

      <FeatureFooterNav next={{ to: "/features/business-profiles", label: "Business Profiles", desc: "Give your startup, company, or organization a home of its own." }} />
    </div>
  );
}
